import React from "react";
import { Link } from "react-router-dom";
import { Clock, BookOpen, Download, ArrowRight } from "lucide-react";

// One card, four type variants. Same padding / radius / border / rhythm across all.
// White page + ivory card fill (1.06:1) => the 1px --border does the structural work.
const BASE =
  "group flex h-full flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-accent/60";

function Meta({ children }) {
  return <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold uppercase tracking-wider text-muted">{children}</div>;
}

function Shell({ to, href, children, testid }) {
  const cls = BASE;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
  return <div data-testid={testid} className={cls}>{children}</div>;
}

export function ContentCard({ type = "article", data = {}, to, href, testid }) {
  if (type === "article") {
    return (
      <Shell to={to} testid={testid}>
        <Meta>
          {data.category && <span className="text-accent">{data.category}</span>}
        </Meta>
        <h3 className="font-heading text-lg font-bold leading-snug text-ink group-hover:text-accent">{data.title}</h3>
        {data.summary && <p className="mt-2 line-clamp-3 flex-1 text-sm text-muted">{data.summary}</p>}
        {data.reading_time && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted"><Clock className="h-3.5 w-3.5" strokeWidth={2} /> {data.reading_time}</p>
        )}
      </Shell>
    );
  }
  if (type === "course") {
    return (
      <Shell to={to} testid={testid}>
        {data.availability && <Meta><span className={data.published ? "text-accent" : "text-muted"}>{data.availability}</span></Meta>}
        <h3 className="font-heading text-lg font-bold leading-snug text-ink group-hover:text-accent">{data.outcome || data.title}</h3>
        {data.summary && <p className="mt-2 flex-1 text-sm text-muted">{data.summary}</p>}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
          {data.lessons != null && <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" strokeWidth={2} /> <span className="tnum">{data.lessons}</span> lessons</span>}
          {data.format && <span>{data.format}</span>}
        </div>
      </Shell>
    );
  }
  if (type === "casestudy") {
    return (
      <Shell to={to} testid={testid}>
        {data.problem && <Meta><span className="text-accent">{data.problem}</span></Meta>}
        <h3 className="font-heading text-lg font-bold leading-snug text-ink">{data.title}</h3>
        {data.approach && <p className="mt-2 flex-1 text-sm text-muted">{data.approach}</p>}
        {/* outcome shown ONLY when a real verified value exists — never a placeholder */}
        {data.outcome && (
          <p className="mt-3 border-t border-border pt-3 text-sm font-semibold text-ink">
            <span className="text-muted">Outcome: </span>{data.outcome}
          </p>
        )}
      </Shell>
    );
  }
  // resource
  return (
    <Shell to={to} href={href} testid={testid}>
      <Meta>{data.format && <span className="text-accent">{data.format}</span>}</Meta>
      <h3 className="font-heading text-lg font-bold leading-snug text-ink group-hover:text-accent">{data.title}</h3>
      {data.receives && <p className="mt-2 flex-1 text-sm text-muted">{data.receives}</p>}
      <p className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-accent">
        {data.available === false ? <>Coming soon</> : <><Download className="h-4 w-4" strokeWidth={2} /> {data.action || "Download"}</>}
        {data.available !== false && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />}
      </p>
    </Shell>
  );
}

export default ContentCard;
